import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Checkbox, FormControlLabel, Grid, Paper, IconButton, Tooltip } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { extractAndTranslateError } from '../../utils/i18nHelpers';

interface Role {
  id: number;
  name: string;
  description?: string;
  permissions?: { resource: string; action: string }[];
  is_system?: boolean;
}

const resources: { key: string; actions: string[] }[] = [
  { key: 'dashboard', actions: ['view'] },
  { key: 'buildings', actions: ['view','add','edit','delete','export-excel','export-pdf'] },
  { key: 'flats', actions: ['view','add','edit','delete','export-excel','export-pdf'] },
  { key: 'work-orders', actions: ['view','add','edit','delete','check-in','check-out','pause','resume','add-expense','export-excel','export-pdf'] },
  { key: 'service-providers', actions: ['view','add','edit','delete','export-excel','export-pdf'] },
  { key: 'expenses', actions: ['view','add','edit','delete','export-excel','export-pdf'] },
  { key: 'quick-check-ins', actions: ['view','add'] },
  { key: 'out-of-site-tasks', actions: ['view','add','edit','delete','reports'] },
  { key: 'inventory', actions: ['view','add','edit','delete','export-excel','export-pdf'] },
  { key: 'roles', actions: ['view','add','edit','delete'] },
  { key: 'users', actions: ['view','add','edit','delete','export-excel','export-pdf'] },
  { key: 'attendance', actions: ['view', 'mark', 'manage'] },
  { key: 'reports', actions: ['view'] },
  { key: 'settings', actions: ['view','edit','update.app_name','update.logo','update.favicon','update.theme','update.check_distance','update.sidebar_light_bg','update.sidebar_light_text','update.sidebar_dark_bg','update.sidebar_dark_text'] },
];


const Roles: React.FC = () => {
  const { can } = useAuth();
  const { t, language } = useLanguage();
  const { notifySuccess, notifyError } = useNotification();
  
  // Format resource name for display
  const formatResourceLabel = (resourceKey: string): string => {
    const translationKey = `roles.resource.${resourceKey}`;
    const translated = t(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }
    // Fallback: convert hyphenated key to Title Case
    return resourceKey.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Format permission labels for better readability with translations
  const formatPermissionLabel = (action: string): string => {
    // Try to get translation first
    const translationKey = `roles.permissions.${action}`;
    const translated = t(translationKey);
    
    // If translation exists and is different from the key, use it
    if (translated && translated !== translationKey) {
      return translated;
    }
    
    // Fallback to formatting if no translation
    // Handle update.* permissions
    if (action.startsWith('update.')) {
      const field = action.replace('update.', '');
      // Convert snake_case to Title Case
      return `Update ${field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`;
    }
    // Handle other actions with hyphens
    return action.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const [roles, setRoles] = useState<Role[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);

  const fetchRoles = async () => {
    const res = await api.get('/roles');
    setRoles(res.data || []);
  };

  useEffect(() => {
    if (can('roles.view')) {
      fetchRoles();
    }
  }, []);

  const openDialog = (role?: Role) => {
    if (role) {
      setEditing(role);
      setName(role.name);
      setDescription(role.description || '');
      const perms = (role.permissions || []).map(p => `${p.resource}.${p.action}`);
      setSelectedPerms(perms);
    } else {
      setEditing(null);
      setName('');
      setDescription('');
      setSelectedPerms([]);
    }
    setOpen(true);
  };

  const closeDialog = () => setOpen(false);

  const togglePerm = (key: string) => {
    setSelectedPerms(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const saveRole = async () => {
    if (!name || name.trim() === '') {
      alert(t('roles.messages.nameRequired'));
      return;
    }
    
    try {
      if (editing) {
        await api.put(`/roles/${editing.id}`, { name: name.trim(), description: description?.trim() || null });
        await api.post(`/roles/${editing.id}/permissions`, { permissions: selectedPerms });
      } else {
        const create = await api.post('/roles', { name: name.trim(), description: description?.trim() || null });
        const role = create.data;
        await api.post(`/roles/${role.id}/permissions`, { permissions: selectedPerms });
      }
      await fetchRoles();
      closeDialog();
    } catch (error: any) {
      console.error('Error saving role:', error);
      if (error.response?.data?.errors) {
        const errorMessages = Object.entries(error.response.data.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        alert(t('roles.messages.save.validationErrors') + '\n' + errorMessages);
      } else if (error.response?.data?.message) {
        alert(t('roles.messages.save.errorPrefix') + ' ' + error.response.data.message);
      } else {
        alert(t('roles.messages.save.genericError'));
      }
    }
  };

  const deleteRole = async (id: number) => {
    if (!can('roles.delete')) return;
    if (window.confirm(t('roles.messages.delete.confirm'))) {
      try {
        await api.delete(`/roles/${id}`);
        notifySuccess(t('roles.messages.delete.success'));
        await fetchRoles();
      } catch (error: any) {
        const errorMessage = extractAndTranslateError(error, language, t);
        notifyError(errorMessage || t('roles.messages.delete.error'));
      }
    }
  };

  if (!can('roles.view')) {
    return <Typography>{t('roles.access.denied')}</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('roles.title')}</Typography>
        {can('roles.add') && (
          <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
            {t('roles.actions.add')}
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        {roles.map(role => (
          <Grid size={{ xs: 12, md: 6 }} key={role.id}>
            <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">{role.name}</Typography>
                {role.description && <Typography variant="body2" color="text.secondary">{role.description}</Typography>}
              </Box>
              <Box>
                {can('roles.edit') && (
                  <IconButton onClick={() => openDialog(role)}><Edit /></IconButton>
                )}
                {can('roles.delete') && (
                  <Tooltip title={role.is_system ? t('roles.messages.delete.systemRoleTooltip') : ''}>
                    <span>
                      <IconButton 
                        color="error" 
                        onClick={() => deleteRole(role.id)}
                        disabled={role.is_system}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? t('roles.dialog.editTitle') : t('roles.dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('roles.dialog.name')}
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            <TextField
              label={t('roles.dialog.description')}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
              <Typography variant="h6">
                {t('roles.dialog.permissionsTitle')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => {
                  const allPerms = resources.flatMap(res => res.actions.map(action => `${res.key}.${action}`));
                  setSelectedPerms(allPerms);
                }}>
                  {t('roles.dialog.selectAll') || 'Select All'}
                </Button>
                <Button size="small" variant="outlined" onClick={() => setSelectedPerms([])}>
                  {t('roles.dialog.removeAll') || 'Remove All'}
                </Button>
              </Box>
            </Box>
            {resources.map(res => (
              <Box key={res.key} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom>{formatResourceLabel(res.key)}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {res.actions.map(action => {
                    const key = `${res.key}.${action}`;
                    return (
                      <FormControlLabel
                        key={key}
                        control={<Checkbox checked={selectedPerms.includes(key)} onChange={() => togglePerm(key)} />}
                        label={formatPermissionLabel(action)}
                      />
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('roles.dialog.cancel')}</Button>
          <Button variant="contained" onClick={saveRole}>
            {editing ? t('roles.dialog.update') : t('roles.dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles;


